import { useMemo } from "react";
import { Avatar, Em, Flex, Separator, Text } from "@radix-ui/themes";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { buildColorMap, RadixColor } from "@/components/radixColors";
import { AvgQaScoreFormatter } from "@/v2/components/formatters/AvgQaScoreFormatter";
import { Collaborator } from "../Explore.model";
import { getInitials } from "@/v2/assets/utils";

function CollaboratorInfo({
  name,
  color,
  publicCommentsCount = 0,
  internalCommentsCount = 0,
  avgQaScore,
}: Collaborator & { name: string; color: RadixColor }) {
  const collaboratorInitials = getInitials(name);

  return (
    <Flex direction={"column"} gap={"1"}>
      <Flex gap={"2"}>
        <Avatar
          size={"1"}
          fallback={collaboratorInitials}
          color={color}
          radius={"full"}
        />

        <Text size={"2"} weight={"bold"}>
          {name}
        </Text>
      </Flex>

      <Text size={"2"}>
        {publicCommentsCount > 0
          ? `${publicCommentsCount} Public Messages`
          : "No Public Messages"}
      </Text>

      {internalCommentsCount > 0 && (
        <Text size={"2"}>{`${internalCommentsCount} Internal Messages`}</Text>
      )}

      {avgQaScore != null && (
        <Text size={"2"}>
          <AvgQaScoreFormatter value={avgQaScore} />
        </Text>
      )}
    </Flex>
  );
}

export function CollaboratorsFormatter({ value }: { value?: Collaborator[] }) {
  const collaborators = useMemo(() => {
    if (value == null) return [];

    let unknownAgentIndex = 1;

    return value
      .map((collaborator) => {
        const name =
          collaborator.agentName != null && collaborator.agentName !== ""
            ? collaborator.agentName
            : `Unknown Agent ${unknownAgentIndex++}`;

        return {
          ...collaborator,
          name,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [value]);

  const collaboratorsToColor = useMemo(() => {
    return buildColorMap(collaborators.map(({ name }) => name) ?? []);
  }, [value]);

  if (value == null || collaborators.length === 0) {
    return <Em>No collaborators</Em>;
  }

  return (
    <Flex gap={"1"}>
      {collaborators.slice(0, 3).map((collaborator) => {
        const collaboratorInitials = getInitials(collaborator.name);
        const color = collaboratorsToColor[collaborator.name];

        return (
          <HoverCard key={collaborator.agentId}>
            <HoverCardTrigger>
              <Avatar
                size={"1"}
                fallback={collaboratorInitials}
                color={color}
                radius={"full"}
              />
            </HoverCardTrigger>

            <HoverCardContent side="top" align="start">
              <CollaboratorInfo {...collaborator} color={color} />
            </HoverCardContent>
          </HoverCard>
        );
      })}

      {collaborators.length > 3 && (
        <HoverCard>
          <HoverCardTrigger>
            <Avatar
              size={"1"}
              fallback={`+${collaborators.length - 3}`}
              color={"gray"}
              radius={"full"}
            />
          </HoverCardTrigger>

          <HoverCardContent side="bottom" sideOffset={8} align="start">
            <Flex direction={"column"} gap={"1"}>
              {collaborators.slice(3).map((collaborator, index) => {
                const color = collaboratorsToColor[collaborator.name];

                return (
                  <>
                    {index !== 0 && <Separator my="3" size="4" />}
                    <CollaboratorInfo {...collaborator} color={color} />
                  </>
                );
              })}
            </Flex>
          </HoverCardContent>
        </HoverCard>
      )}
    </Flex>
  );
}
